import '../../styles/components/admin.css'
import { requireAuth, getCategories, createCategory, updateCategory, deleteCategory } from '../../lib/supabase.js'

export function renderAdminCategories(outlet) {
  requireAuth(async () => {
    outlet.innerHTML = `
      <div class="admin-page">
        <div class="container">
          <div class="admin-topbar" style="margin-bottom: var(--space-6);">
            <h1 class="admin-title">Categories</h1>
            <a href="#/admin" class="admin-back">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" width="16" height="16">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5"/>
              </svg>
              Back to Products
            </a>
          </div>
          <div id="admin-cat-error" class="admin-error" style="display:none; margin-bottom: var(--space-4);"></div>
          <div id="admin-cat-content">
            <div class="products-loading" style="padding: var(--space-16) 0; text-align:center;">
              <div class="spinner"></div>
            </div>
          </div>
        </div>
      </div>
    `
    await loadCategories()
  })
}

async function loadCategories() {
  const content = document.getElementById('admin-cat-content')
  if (!content) return

  try {
    const raw = await getCategories()
    // getCategories returns objects from the table or strings as fallback
    const categories = raw.map(c => typeof c === 'string' ? { id: c, name: c, sort_order: 0 } : c)

    content.innerHTML = `
      <form id="add-cat-form" style="display:flex; gap: var(--space-3); margin-bottom: var(--space-6); align-items:flex-end;">
        <div class="form-group" style="flex:1; margin:0;">
          <label class="form-label" for="new-cat-name">New Category</label>
          <input id="new-cat-name" class="form-input" type="text" placeholder="e.g. accessories" required />
        </div>
        <div class="form-group" style="width:130px; margin:0;">
          <label class="form-label" for="new-cat-order">Sort Order</label>
          <input id="new-cat-order" class="form-input" type="number" value="0" min="0" step="10" />
        </div>
        <button type="submit" class="btn btn-primary">Add</button>
      </form>

      <div class="admin-table-wrap">
        <table class="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th style="width:130px;">Sort Order</th>
              <th style="width:160px;">Actions</th>
            </tr>
          </thead>
          <tbody id="cat-tbody">
            ${categories.length === 0
              ? '<tr><td colspan="3" style="text-align:center; color: var(--color-text-secondary); padding: var(--space-8);">No categories yet.</td></tr>'
              : categories.map(c => renderRow(c)).join('')
            }
          </tbody>
        </table>
      </div>
    `

    document.getElementById('add-cat-form').addEventListener('submit', async (e) => {
      e.preventDefault()
      const nameInput = document.getElementById('new-cat-name')
      const orderInput = document.getElementById('new-cat-order')
      const name = nameInput.value.trim()
      const sort_order = parseInt(orderInput.value, 10) || 0
      if (!name) return
      const submitBtn = e.target.querySelector('[type=submit]')
      submitBtn.disabled = true
      submitBtn.textContent = 'Adding…'
      hideError()
      try {
        const cat = await createCategory({ name, sort_order })
        const tbody = document.getElementById('cat-tbody')
        const empty = tbody.querySelector('td[colspan]')
        if (empty) empty.closest('tr').remove()
        tbody.insertAdjacentHTML('beforeend', renderRow(cat))
        nameInput.value = ''
        orderInput.value = '0'
        nameInput.focus()
      } catch (err) {
        showError(err.message || 'Failed to create category')
      } finally {
        submitBtn.disabled = false
        submitBtn.textContent = 'Add'
      }
    })

    document.getElementById('cat-tbody').addEventListener('click', async (e) => {
      const editBtn = e.target.closest('[data-edit]')
      const cancelBtn = e.target.closest('[data-cancel]')
      const saveBtn = e.target.closest('[data-save]')
      const deleteBtn = e.target.closest('[data-delete]')

      if (editBtn) enterEditMode(editBtn.dataset.edit)

      if (cancelBtn) {
        const id = cancelBtn.dataset.cancel
        const row = document.getElementById(`cat-row-${id}`)
        const name = row?.dataset.name || ''
        const sort_order = parseInt(row?.dataset.order || '0', 10)
        row.outerHTML = renderRow({ id, name, sort_order })
      }

      if (saveBtn) {
        const id = saveBtn.dataset.save
        const name = document.getElementById(`cat-name-${id}`)?.value.trim()
        const sort_order = parseInt(document.getElementById(`cat-order-${id}`)?.value, 10) || 0
        if (!name) return
        saveBtn.disabled = true
        saveBtn.textContent = 'Saving…'
        hideError()
        try {
          const updated = await updateCategory(id, { name, sort_order })
          document.getElementById(`cat-row-${id}`).outerHTML = renderRow(updated)
        } catch (err) {
          showError(err.message || 'Failed to update category')
          saveBtn.disabled = false
          saveBtn.textContent = 'Save'
        }
      }

      if (deleteBtn) {
        const id = deleteBtn.dataset.delete
        const name = deleteBtn.dataset.name
        if (!confirm(`Delete "${name}"? Products using this category will keep the value.`)) return
        deleteBtn.disabled = true
        deleteBtn.textContent = 'Deleting…'
        hideError()
        try {
          await deleteCategory(id)
          document.getElementById(`cat-row-${id}`)?.remove()
        } catch (err) {
          showError(err.message || 'Failed to delete category')
          deleteBtn.disabled = false
          deleteBtn.textContent = 'Delete'
        }
      }
    })

  } catch (err) {
    content.innerHTML = `<div class="admin-error">Failed to load categories: ${escapeHtml(err.message)}</div>`
  }
}

function renderRow(c) {
  return `
    <tr id="cat-row-${c.id}" data-name="${escapeHtml(c.name)}" data-order="${c.sort_order}">
      <td>${escapeHtml(c.name)}</td>
      <td>${c.sort_order}</td>
      <td class="cell-actions">
        <button class="btn btn-sm" style="font-size: var(--text-xs);" data-edit="${c.id}">Edit</button>
        <button class="btn btn-sm" style="font-size: var(--text-xs); border-color: var(--color-error); color: var(--color-error);"
          data-delete="${c.id}" data-name="${escapeHtml(c.name)}">Delete</button>
      </td>
    </tr>
  `
}

function enterEditMode(id) {
  const row = document.getElementById(`cat-row-${id}`)
  if (!row) return
  const name = row.dataset.name
  const order = row.dataset.order
  const [nameCell, orderCell, actionsCell] = row.querySelectorAll('td')
  nameCell.innerHTML = `<input id="cat-name-${id}" class="form-input" type="text" value="${escapeHtml(name)}" style="padding: var(--space-1) var(--space-2); height:34px;" />`
  orderCell.innerHTML = `<input id="cat-order-${id}" class="form-input" type="number" value="${order}" style="padding: var(--space-1) var(--space-2); height:34px; width:90px;" />`
  actionsCell.innerHTML = `
    <button class="btn btn-sm btn-primary" style="font-size: var(--text-xs);" data-save="${id}">Save</button>
    <button class="btn btn-sm" style="font-size: var(--text-xs);" data-cancel="${id}">Cancel</button>
  `
  document.getElementById(`cat-name-${id}`)?.focus()
}

function showError(msg) {
  const el = document.getElementById('admin-cat-error')
  if (!el) return
  el.textContent = msg
  el.style.display = 'block'
}

function hideError() {
  const el = document.getElementById('admin-cat-error')
  if (el) el.style.display = 'none'
}

function escapeHtml(str) {
  return String(str || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}
